import { OrdersDataByStatus } from '@linka/domain/dtos';
import { OrderDisplayStatus, PaymentMethod } from '@linka/domain/enums';
import { eventDashboardQueries } from '@linka/http/event-dashboard';
import { useTranslation } from '@linka/i18n';
import { formatDate } from '@linka/utils';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useParams, useSearchParams } from 'react-router';
import { ColumnDef, DataTable } from '../../../data-table/data-table';
import { Breadcrumb } from '../breadcrumb';
import { ExportChartTriggerButton } from '../components';
import { ExportFlowWrapper } from '../export-flow-wrapper';
import {
  DashboardFilters,
  DashboardFiltersDatePicker,
  DashboardFiltersPaymentMethods,
  DashboardFiltersTickets,
  dashboardFiltersAtom,
} from '../filters';
import { useSystemFilters } from '../hooks/use-system-filters';
import { buildTrackingColumns } from './tracking-columns';

interface DetailedOrdersProps {
  userEmail?: string;
}

export const DetailedOrders = ({ userEmail }: DetailedOrdersProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const t = useTranslation();
  const { eventId = '' } = useParams();
  const dashboardFilters = useAtomValue(dashboardFiltersAtom);
  const systemFilters = useSystemFilters();
  const setFilter = (params: Record<string, string>) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(params).forEach(([k, v]) => next.set(k, v));
      return next;
    });
  const status = searchParams.get('status') as OrderDisplayStatus;
  const { data: orders, isPending } = useQuery(
    eventDashboardQueries.orders(eventId, {
      status,
      ...dashboardFilters,
      ...systemFilters,
    }),
  );

  const { data: trackingColumns = [] } = useQuery(eventDashboardQueries.trackingColumns(eventId));

  const columns: ColumnDef<OrdersDataByStatus>[] = [
    {
      accessorKey: 'orderId',
      header: t('order-id'),
    },
    {
      accessorKey: 'saleDate',
      header: t('date'),
      cell: ({ row }) => <span>{formatDate({ date: row.original.saleDate, dateFormat: 'dd/MM/yyyy HH:mm' })}</span>,
    },
    {
      accessorKey: 'paymentMethod',
      header: t('payment-method'),
      cell: ({ row }) => <span>{t(row.original.paymentMethod as PaymentMethod)}</span>,
    },
    {
      accessorKey: 'ticketTitle',
      header: t('ticket'),
    },
    {
      accessorKey: 'daysSincePurchase',
      header: t('dashboard.detailed.time-since-purchase'),
    },
    {
      accessorKey: 'buyerEmail',
      header: t('dashboard.detailed.buyer-email'),
    },
    {
      accessorKey: 'buyerName',
      header: t('dashboard.detailed.buyer-name'),
    },
    {
      accessorKey: 'buyerPhone',
      header: t('dashboard.detailed.buyer-phone'),
      minSize: 350,
    },
    {
      accessorKey: 'participantName',
      header: t('dashboard.detailed.participant-name'),
    },
    {
      accessorKey: 'participantEmail',
      header: t('dashboard.detailed.participant-email'),
    },
    {
      accessorKey: 'participantPhone',
      header: t('dashboard.detailed.participant-phone'),
    },
    {
      accessorKey: 'status',
      header: t('dashboard.detailed.order-status'),
      cell: ({ row }) => <span>{t(row.original.status as OrderDisplayStatus)}</span>,
    },
    ...buildTrackingColumns<OrdersDataByStatus>(trackingColumns),
  ];

  return (
    <div>
      <Breadcrumb hideHome />
      <header className='grid sm:grid-cols-[1fr,max-content] items-center gap-y-mini my-xxs'>
        <h1 className='ts-heading-sm sm:ts-heading-md text-ctx-content-title'>
          {t('dashboard.detailed.orders-status')}
        </h1>
        <div className='ml-auto sm:ml-0 sm:mr-auto sm:col-start-1'>
          <DashboardFilters>
            <DashboardFiltersDatePicker />
            <DashboardFiltersPaymentMethods />
            <DashboardFiltersTickets />
          </DashboardFilters>
        </div>
        <ExportChartTriggerButton type='ORDERS_CHECKOUT' className='col-span-2 self-end sm:col-span-1' />
      </header>
      <DataTable
        isPending={isPending}
        columns={columns}
        perPage={10}
        total={orders?.total}
        data={orders?.data || []}
        orderBy={systemFilters.orderBy}
        sortOrder={systemFilters.sortOrder}
        onSortChange={(col, dir) => setFilter({ orderBy: col, sortOrder: dir })}
        page={systemFilters.page}
        onPageChange={(page) => setFilter({ page: page.toString() })}
      />
      <ExportFlowWrapper userEmail={userEmail} />
    </div>
  );
};
